import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { Injectable, signal } from '@angular/core';

@Injectable()
export abstract class BaseEntityService<T, TFilter = any> {
  protected results$ = new BehaviorSubject<T[]>([]);
  protected isAdding$ = new BehaviorSubject<boolean>(true);
  protected selectedId$ = new BehaviorSubject<string | null>(null);
  protected behaviorReset$ = new Subject<void>();

  private readonly resultsObservable$ = this.results$.asObservable();
  private readonly isAddingObservable$ = this.isAdding$.asObservable();
  private readonly selectedIdObservable$ = this.selectedId$.asObservable();
  private readonly behaviorResetObservable$ =
    this.behaviorReset$.asObservable();

  protected loadingList$ = signal<boolean>(false);
  protected filters$ = signal<TFilter | null>(null);
  // For gets or custom actions
  protected actionLoading$ = signal<boolean>(false);
  protected total$ = signal<number>(0);
  protected formLoading$ = signal<boolean>(false);
  protected isFormDirty$ = signal<boolean>(false);
  protected hasWritePermission$ = signal<boolean>(true);
  protected hasDeletePermission$ = signal<boolean>(false);

  constructor() {}

  get total() {
    return this.total$();
  }

  set total(value: number) {
    this.total$.set(value);
  }

  get resultsObservable(): Observable<T[]> {
    return this.resultsObservable$;
  }

  get isAddingObservable(): Observable<boolean> {
    return this.isAddingObservable$;
  }

  get selectedIdObservable(): Observable<string | null> {
    return this.selectedIdObservable$;
  }

  get behaviorReset(): Observable<void> {
    return this.behaviorResetObservable$;
  }

  triggerBehaviorReset() {
    this.behaviorReset$.next();
  }

  get results() {
    return this.results$.getValue();
  }

  set results(value: T[]) {
    const values = this.results$.value;

    values.push(...value);
    this.results$.next(values);
  }

  cleanResults() {
    this.results$.next([]);
  }

  /** Adds items at the start of the results (used for backward pagination). */
  protected prependResults(items: T[]): void {
    this.results$.next([...items, ...this.results$.value]);
  }

  get isAdding() {
    return this.isAdding$.getValue();
  }

  set isAdding(value: boolean) {
    this.isAdding$.next(value);
    if (value) {
      this.selectedId = null;
    }
  }

  get selectedId() {
    return this.selectedId$.getValue();
  }

  set selectedId(value: string | null) {
    this.selectedId$.next(value);

    if (value) {
      this.isAdding = false;
    }
  }

  get loadingList() {
    return this.loadingList$();
  }

  set loadingList(value: boolean) {
    this.loadingList$.set(value);
  }

  get actionLoading() {
    return this.actionLoading$();
  }

  set actionLoading(value: boolean) {
    this.actionLoading$.set(value);
  }

  get formLoading() {
    return this.formLoading$();
  }

  set formLoading(value: boolean) {
    this.formLoading$.set(value);
  }

  // 5. Form Dirty
  get isFormDirty() {
    return this.isFormDirty$();
  }

  set isFormDirty(value: boolean) {
    this.isFormDirty$.set(value);
  }

  get hasWritePermission() {
    return this.hasWritePermission$();
  }

  set hasWritePermission(value: boolean) {
    this.hasWritePermission$.set(value);
  }

  get hasDeletePermission() {
    return this.hasDeletePermission$();
  }

  set hasDeletePermission(value: boolean) {
    this.hasDeletePermission$.set(value);
  }

  get filters() {
    return this.filters$();
  }

  set filters(value: TFilter | null) {
    this.filters$.set(value);
  }

  resetDefaults(): void {
    this.cleanResults();
    this.isAdding = true;
    this.isFormDirty = false;
    this.selectedId = null;
    this.loadingList = false;
    this.actionLoading = false;
    this.total = 0;
    this.formLoading = false;
    this.hasWritePermission = true;
    this.hasDeletePermission = true;
    this.filters = null;
  }
}
